import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { ImportIntegration } from '@app/models/import/import-integration.model';
import { ImportIntegrationService } from '@app/modules/import/services';
import { IntegrationStep } from '@app/modules/import/services/integration/import-integration.service';
import { ImportEmployeeTable } from '@app/modules/import/services/tables/import-employee-table/import-employee.table';
import { TableModule } from '@app/modules/table/table.module';
import { PlatformModule } from '@app/platform.module';
import { ImportSummary } from '@app/types/metadata.type';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { INTEGRATIONS_HELP_LINK } from '../../integrations-import.view';

@Component({
    selector: 'app-integrations-import-preview',
    templateUrl: './integrations-import-preview.template.html',
    standalone: true,
    imports: [CommonModule, TranslateModule, PlatformModule, TableModule],
    styleUrls: ['./integrations-import-preview.style.scss', '../integration-steps.scss'],
})
export class IntegrationsImportPreviewStep implements OnDestroy {
    integrationsHelpLink = INTEGRATIONS_HELP_LINK;
    loadingLevel: 'INTEGRATION' | 'EMPLOYEES' | null = null;
    importEmployeeTable?: ImportEmployeeTable;

    private _importSummary?: ImportSummary;
    private _destroyed$: Subject<void> = new Subject<void>();
    private _isErrored = false;
    private _integration: ImportIntegration | null = null;

    get isErrored(): boolean {
        return this._isErrored;
    }

    get integration(): ImportIntegration | null {
        return this._integration;
    }

    get importSummary(): ImportSummary | undefined {
        return this._importSummary;
    }

    constructor(public integrationService: ImportIntegrationService) {
        this.integrationService.integrationInProgress.pipe(takeUntil(this._destroyed$)).subscribe((integration) => {
            if (!integration) {
                this.loadingLevel = 'INTEGRATION';
                return;
            }
            this._integration = integration;
            this.importEmployeeTable = new ImportEmployeeTable(this._integration);
            this.subscribeToTableLoadingState();
        });
    }

    subscribeToTableLoadingState(): void {
        this.importEmployeeTable?.onLoading
            .pipe(
                takeUntil(this._destroyed$),
                catchError(() => {
                    this._isErrored = true;
                    this.loadingLevel = null;
                    return of(undefined);
                })
            )
            .subscribe((isLoading) => {
                if (isLoading === undefined) {
                    return;
                }
                if (isLoading) {
                    this.loadingLevel = 'EMPLOYEES';
                } else {
                    const summary = this.importEmployeeTable?.paginator?.meta?.summary;
                    if (summary) {
                        this._importSummary = summary;
                    }
                    this.loadingLevel = null;
                }
            });
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    onBack(): void {
        this.integrationService.goToStep(IntegrationStep.LIST);
    }

    onIntegrationAction(): void {
        this.integrationService.doImport();
    }
}
